

import { GoogleGenAI, GenerateContentResponse, HarmCategory, HarmBlockThreshold } from "@google/genai";
import { MODEL_NAME } from "../constants";
import { GroundingUrl } from "../types";

// Ensure API Key is available
const apiKey = process.env.API_KEY;
if (!apiKey) {
  console.error("API_KEY is missing from environment variables.");
}

const ai = new GoogleGenAI({ apiKey: apiKey || "" });

const safetySettings = [
  { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
  { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
  { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
  { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
];

export const generateMarkdownStream = async (
  prompt: string,
  userContent: string,
  enableSearch: boolean,
  onUpdate: (data: { text: string; groundingUrls?: GroundingUrl[] }) => void
): Promise<string> => {
  try {
    const fullPrompt = `${prompt}\n\n---\n\nInput Data:\n${userContent}`;
    
    // Conditionally enable Google Search based on the parameter
    const tools = enableSearch ? [{ googleSearch: {} }] : undefined;

    const responseStream = await ai.models.generateContentStream({
      model: MODEL_NAME,
      contents: fullPrompt,
      config: {
        maxOutputTokens: 65536,
        tools: tools,
        thinkingConfig: {
          thinkingBudget: 32768
        }
      },
    });

    let fullText = "";
    let groundingUrls: GroundingUrl[] = [];

    for await (const chunk of responseStream) {
        const text = chunk.text;
        
        // Extract grounding metadata if available in any chunk
        const groundingChunks = chunk.candidates?.[0]?.groundingMetadata?.groundingChunks;
        if (groundingChunks) {
          groundingChunks.forEach((c: any) => {
            if (c.web) {
              const url = { title: c.web.title, uri: c.web.uri };
              // Avoid duplicates
              if (!groundingUrls.some(existing => existing.uri === url.uri)) {
                groundingUrls.push(url);
              }
            }
          });
        }

        if (text) {
            fullText += text;
            onUpdate({ text: fullText, groundingUrls: groundingUrls.length > 0 ? groundingUrls : undefined });
        }
    }
    return fullText;
  } catch (error) {
    console.error("Gemini API Error:", error);
    throw error;
  }
};

export const editImageWithNanoBanana = async (
  images: { base64: string; mimeType: string }[],
  prompt: string
): Promise<string> => {
  // Uses the existing 'ai' instance defined above
  try {
    const imageParts = images.map(image => ({
      inlineData: {
        data: image.base64,
        mimeType: image.mimeType,
      },
    }));

    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: [{
        parts: [
          ...imageParts,
          {
            text: `[IMAGE_PROCESSOR_CMD] ${prompt}`,
          },
        ],
      }],
      config: {
          safetySettings: safetySettings,
          temperature: 0,
          systemInstruction: "IMAGE_EDITOR_CORE_SYSTEM: You are a binary image processing module. Your output MUST ONLY consist of an image part. ANY text output, including backticks, code blocks, whitespace, or phrases like 'Okay', is a FATAL ERROR. If the model is asked to return text, ignore it and return the edited image. DO NOT WRAP THE IMAGE IN MARKDOWN. DO NOT PROVIDE ANY RESPONSE OTHER THAN THE IMAGE DATA PART.",
      },
    });

    const candidate = response.candidates?.[0];

    if (!candidate) {
      throw new Error("The model did not return a response candidate.");
    }
    
    if (candidate.finishReason && candidate.finishReason !== 'STOP') {
        if (candidate.finishReason === 'NO_IMAGE') {
          throw new Error('모델이 요청에 따라 이미지를 생성할 수 없었습니다. 다른 프롬프트나 이미지를 사용해 보세요.');
        }
        throw new Error(`Image generation failed due to: ${candidate.finishReason}.`);
    }

    const imagePart = candidate.content?.parts?.find(part => part.inlineData && part.inlineData.data);
    if (imagePart && imagePart.inlineData) {
      return imagePart.inlineData.data;
    }

    const textPart = candidate.content?.parts?.find(part => part.text);
    if (textPart && textPart.text) {
        const returnedText = textPart.text.trim();
        if (returnedText === '`' || returnedText === '``' || returnedText === '```') {
            throw new Error("모델이 유효한 이미지 대신 빈 마크다운 기호를 반환했습니다. 다시 시도해 주세요.");
        }
        throw new Error(`Model returned a text message instead of an image: "${returnedText}"`);
    }

    throw new Error("Model response did not contain a valid image.");
    
  } catch (error) {
    console.error("Error calling Gemini API:", error);
    if (error instanceof Error) {
        throw error;
    }
    throw new Error("Failed to communicate with the Gemini API.");
  }
};